import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { type ComponentProps, useEffect, useRef } from 'react';

import { cn } from '../lib/cn';

echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  CanvasRenderer,
]);

export type EChartOption = echarts.EChartsCoreOption;

export interface EChartProps extends Omit<ComponentProps<'div'>, 'children'> {
  option: EChartOption;
  /** 'dark' | 'light': al cambiar se recrea la instancia con el tema. */
  theme?: 'dark' | 'light';
}

/** Envoltorio mínimo de ECharts: init/dispose, resize automático y cambio de tema. */
export function EChart({ option, theme = 'light', className, ...props }: EChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const chart = echarts.init(el, theme === 'dark' ? 'dark' : undefined, { renderer: 'canvas' });
    chartRef.current = chart;
    const observer = new ResizeObserver(() => {
      chart.resize();
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, [theme]);

  useEffect(() => {
    chartRef.current?.setOption({ backgroundColor: 'transparent', ...option }, true);
  }, [option, theme]);

  return <div ref={ref} className={cn('h-64 w-full', className)} {...props} />;
}
